import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, Info, ShieldCheck, Share2, Plus, FileText, Map, ExternalLink, Youtube, Heart, Zap, Clock } from 'lucide-react';
import { getProductImageUrl, getProductImages } from '../utils/images';


const ProductDetailView = ({ product, setView, addToCart, handleShare, formatPrice, favorites, toggleFavorite, isAdmin, isFlashSaleActive, isFlashSaleComingSoon, flashSaleSettings }) => {
  const [activeMedia, setActiveMedia] = useState(0); 
  const [showVideo, setShowVideo] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [warrantySelected, setWarrantySelected] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');

  const isFav = Array.isArray(favorites) && favorites.find(f => f.id === product?.id);
  
  // Utilise la fonction passée ou une logique par défaut si non fournie
  const flashActive = typeof isFlashSaleActive === 'function' ? isFlashSaleActive(product) : product?.isFlashSale;
  
  const flashScheduled = typeof isFlashSaleComingSoon === 'function' ? isFlashSaleComingSoon(product) : false;

  useEffect(() => {
    if (!flashActive || !product?.flashSaleEndDate) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const end = new Date(product.flashSaleEndDate).getTime();
      const distance = end - now;

      if (distance < 0) {
        setTimeLeft('Terminé');
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      let timeStr = '';
      if (days > 0) timeStr += `${days}j `;
      timeStr += `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      setTimeLeft(timeStr);
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [flashActive, product?.flashSaleEndDate]);

  // Calcul du stock disponible pour les options sélectionnées
  const currentStock = useMemo(() => {
    if (!product) return 0;
    if (!Array.isArray(product.variants) || product.variants.length === 0) return product.stock || 0;
    
    // Trouver la variante qui correspond à toutes les options sélectionnées
    const variant = product.variants.find(v => 
      Object.entries(selectedOptions).length === (Array.isArray(product.options) ? product.options.length : 0) &&
      Object.entries(selectedOptions).every(([name, val]) => v.options?.[name] === val)
    );

    return variant ? variant.stock : (Object.entries(selectedOptions).length > 0 ? 0 : product.stock);
  }, [product, selectedOptions]);

  // Calcul du prix dynamique basé sur les options
  const currentPrice = useMemo(() => {
    if (!product) return 0;
    let total = product.price;
    
    // Prix de base des options
    if (product.options) {
      product.options.forEach(opt => {
        const selectedValue = selectedOptions[opt.name];
        if (selectedValue) {
          const valObj = opt.values.find(v => v.label === selectedValue);
          if (valObj && valObj.priceMod) total += valObj.priceMod;
        }
      });
    }

    // Modificateur spécifique à la variante (combinaison)
    if (product.variants) {
      const variant = product.variants.find(v => 
        Object.entries(selectedOptions).every(([name, val]) => v.options[name] === val)
      );
      if (variant && variant.priceMod) total += variant.priceMod;
    }

    if (warrantySelected && product.warrantyExtension?.enabled) {
      total += product.warrantyExtension.price;
    }

    if (flashActive && product.flashSaleDiscount > 0) {
      total = total * (1 - product.flashSaleDiscount / 100);
    }

    return total;
  }, [product, selectedOptions, warrantySelected, flashActive]);

  if (!product) return null;

  const isOnOrder = Boolean(product.onOrder);
  const isVariantOutOfStock = currentStock <= 0 && !isOnOrder;
  const productImages = getProductImages(product);

  const handleOptionChange = (optName, value) => {
    setSelectedOptions(prev => ({ ...prev, [optName]: value }));
  };

  const onAddToCart = (e) => {
    // Vérifier que toutes les options obligatoires sont sélectionnées
    if (product.options) {
      const missing = product.options.filter(opt => !selectedOptions[opt.name]);
      if (missing.length > 0) {
        alert(`Veuillez sélectionner : ${missing.map(m => m.name).join(', ')}`);
        return;
      }
    }

    const finalOptions = { ...selectedOptions };
    if (warrantySelected && product.warrantyExtension?.enabled) {
      finalOptions['Garantie'] = `Extension ${product.warrantyExtension.duration}`;
    }

    addToCart(e, product, finalOptions, currentPrice);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20 text-slate-900 dark:text-white">
      <div className="flex justify-between items-center mb-8">
        <button 
          onClick={() => setView('catalog')} 
          className="flex items-center text-slate-500 dark:text-slate-400 font-bold transition-colors hover:text-blue-600 group"
        >
          <ChevronLeft className="group-hover:-translate-x-1 transition-transform" /> Retour au Catalogue
        </button>
        <div className="flex items-center gap-3">
          <button 
            onClick={(e) => toggleFavorite(e, product)} 
            className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold transition-all shadow-sm border ${
              isFav 
                ? 'bg-red-500 text-white border-red-500' 
                : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-800 hover:text-red-500'
            }`}
          >
            <Heart className={`w-4 h-4 ${isFav ? 'fill-current' : ''}`} />
            {isFav ? 'Favori' : 'Ajouter aux favoris'}
          </button>
          <button 
            onClick={(e) => handleShare(e, product)} 
            className="flex items-center gap-2 bg-white dark:bg-slate-900 px-6 py-3 rounded-2xl font-bold text-slate-600 dark:text-slate-300 border dark:border-slate-800 hover:bg-blue-600 hover:text-white transition-all shadow-sm"
          >
            <Share2 className="w-4 h-4" /> Partager
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <div className="space-y-4 lg:col-span-1">
          <div className="relative aspect-square max-w-md mx-auto bg-white dark:bg-slate-900 rounded-2xl border dark:border-slate-800 overflow-hidden shadow-sm flex items-center justify-center">
            {flashActive && (
              <div className="absolute top-6 left-6 z-10 flex flex-col gap-2">
                <div className="bg-amber-500 text-white px-4 py-2 rounded-2xl shadow-lg flex items-center gap-2 animate-pulse">
                  <Zap className="w-4 h-4 fill-current" />
                  <span className="text-[10px] font-black uppercase tracking-widest">Vente Flash</span>
                </div>
                {timeLeft && flashSaleSettings?.showCountdown !== false && (
                  <div className="bg-slate-900/80 backdrop-blur-md text-white px-4 py-1.5 rounded-xl shadow-lg flex items-center gap-2 border border-white/10">
                    <Clock className="w-3 h-3 text-amber-400" />
                    <span className="text-[10px] font-black font-mono">{timeLeft}</span>
                  </div>
                )}
              </div>
            )}
            {flashScheduled && (
              <div className="absolute top-6 left-6 z-10 bg-blue-600 text-white px-4 py-2 rounded-2xl shadow-lg flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-widest">Bientôt en Promo</span>
              </div>
            )}
            {showVideo ? (
              <div className="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-2xl p-8">
                <div className="text-center space-y-4">
                  <Youtube className="w-16 h-16 mx-auto text-red-600" />
                  <h3 className="text-xl font-black dark:text-white">Vidéo de démonstration</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Cette vidéo ne peut pas être intégrée directement.
                  </p>
                  <a 
                    href={product.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-4 rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg"
                  >
                    <Youtube className="w-5 h-5" />
                    Regarder sur YouTube
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => setShowVideo(false)}
                    className="block w-full mt-4 text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 font-bold"
                  >
                    Retour aux images
                  </button>
                </div>
              </div>
            ) : (
                      <img src={productImages[activeMedia] || productImages[0]} className="w-full h-full object-contain p-12" alt={`${product.brand} ${product.label}`.trim() || 'Produit'} />
                    )}
            {product.videoUrl && !showVideo && (
              <button 
                onClick={() => setShowVideo(true)} 
                className="absolute bottom-6 right-6 bg-blue-600 text-white px-6 py-2 rounded-full font-bold shadow-xl"
              >
                Voir la Vidéo
              </button>
            )}
          </div>
          <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {productImages.map((img, idx) => (
              <button 
                key={idx} 
                onClick={() => { setActiveMedia(idx); setShowVideo(false); }} 
                className={`w-14 h-14 md:w-16 md:h-16 rounded-xl border-2 flex-shrink-0 transition-all ${activeMedia === idx ? 'border-blue-600' : 'border-transparent opacity-60 hover:opacity-100'}`}
              >
                <img src={img} className="w-full h-full object-cover rounded-lg" alt={`Miniature ${idx + 1}`} />
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-6 md:space-y-8 text-left lg:col-span-2">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-blue-600 font-black uppercase text-xs tracking-widest">{product.brand}</span>
              {product.isEndOfSeries && (
                <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 px-3 py-1 rounded-full font-black text-[8px] uppercase tracking-widest border border-amber-200 dark:border-amber-800">
                  Fin de série
                </span>
              )}
            </div>
            <h1 className="text-5xl font-black dark:text-white mb-4 tracking-tighter">{product.label}</h1>
            <p className="text-xl text-slate-500 dark:text-slate-400 italic">{product.summary}</p>
          </div>

          {/* Sélecteurs d'options (Variantes) */}
          {product.options && product.options.length > 0 && (
            <div className="space-y-6 bg-slate-50 dark:bg-slate-800/30 p-6 rounded-[32px] border dark:border-slate-800">
              {product.options.map((opt, idx) => (
                <div key={idx} className="space-y-3">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest flex justify-between">
                    {opt.name}
                    <span className="text-blue-600">Requis</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {opt.values.map((val, vIdx) => (
                      <button
                        key={vIdx}
                        onClick={() => handleOptionChange(opt.name, val.label)}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border-2 ${
                          selectedOptions[opt.name] === val.label
                            ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-blue-400'
                        }`}
                      >
                        {val.label}
                        {val.priceMod > 0 && ` (+${formatPrice(val.priceMod)})`}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Extension de Garantie */}
          {product.warrantyExtension?.enabled && (
            <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-[32px] border border-blue-100 dark:border-blue-800/50 space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600 rounded-xl text-white">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-black dark:text-white uppercase tracking-widest">Extension de Garantie</h4>
                    <p className="text-[10px] text-blue-600 dark:text-blue-400 font-bold uppercase tracking-widest">Sécurité supplémentaire</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-lg font-black text-blue-600">+{formatPrice(product.warrantyExtension.price)}</span>
                </div>
              </div>
              
              <button 
                onClick={() => setWarrantySelected(!warrantySelected)}
                className={`w-full p-4 rounded-2xl font-bold text-sm transition-all flex items-center justify-between border-2 ${
                  warrantySelected 
                    ? 'bg-blue-600 border-blue-600 text-white shadow-lg' 
                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-blue-400'
                }`}
              >
                <span>Ajouter l'extension ({product.warrantyExtension.duration})</span>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${warrantySelected ? 'bg-white border-white text-blue-600' : 'border-slate-300 dark:border-slate-600'}`}>
                  {warrantySelected && <Plus className="w-4 h-4 stroke-[4]" />}
                </div>
              </button>
            </div>
          )}

          <div className="bg-white dark:bg-slate-900 p-8 rounded-[32px] border dark:border-slate-800 flex items-center justify-between shadow-sm">
            <div className="flex flex-col">
              {flashActive && product.flashSaleDiscount > 0 ? (
                <>
                  <div className="flex items-center gap-3">
                    <span className="text-4xl font-black dark:text-white">{formatPrice(currentPrice)}</span>
                    <span className="bg-red-500 text-white text-xs font-black px-2 py-1 rounded-xl animate-pulse">
                      -{product.flashSaleDiscount}%
                    </span>
                  </div>
                  <span className="text-lg text-slate-400 line-through font-bold">
                    {formatPrice(currentPrice / (1 - product.flashSaleDiscount / 100))}
                  </span>
                </>
              ) : (
                <span className="text-4xl font-black dark:text-white">{formatPrice(currentPrice)}</span>
              )}
              <span className={`text-[10px] font-black uppercase tracking-widest mt-1 ${isVariantOutOfStock ? 'text-amber-500' : 'text-emerald-500'}`}>
                  {isOnOrder ? 'Sur commande' : (isVariantOutOfStock ? 'Rupture' : `En stock (${currentStock} unités)`) }
                </span>
            </div>
              <div className="flex flex-col items-end gap-3">
                <button 
                  onClick={onAddToCart} 
                  disabled={isVariantOutOfStock}
                  className={`px-10 py-5 rounded-2xl font-black shadow-lg transition-all active:scale-95 ${isVariantOutOfStock ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed' : (isOnOrder ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-blue-600 text-white hover:bg-blue-700')}`}
                >
                  {isOnOrder ? 'Ajouter au Panier (sur commande)' : (isVariantOutOfStock ? 'Rupture' : 'Ajouter au Panier')}
                </button>

                {isVariantOutOfStock && !isOnOrder && (
                  <button
                    onClick={(e) => {
                      // allow ordering despite rupture by marking item as onOrder
                      const finalOptions = { ...selectedOptions };
                      if (warrantySelected && product.warrantyExtension?.enabled) {
                        finalOptions['Garantie'] = `Extension ${product.warrantyExtension.duration}`;
                      }
                      addToCart(e, product, finalOptions, currentPrice, { onOrder: true });
                    }}
                    className="px-6 py-3 rounded-xl font-bold border-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800 hover:bg-amber-100 transition-all"
                  >
                    Commander sur demande
                  </button>
                )}
              </div>
          </div>
          <div className="border-t dark:border-slate-800 pt-8 space-y-6">
            <div>
              <h3 className="font-black dark:text-white mb-4 flex items-center gap-2 uppercase tracking-widest text-xs text-slate-400">
                <Info className="w-4 h-4 text-blue-600" /> Caractéristiques techniques
              </h3>
              <div className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed space-y-6">
                {(() => {
                  if (!product.description) return null;
                  
                  // Détecte si le texte a une structure multi-lignes
                  const hasMultipleLines = product.description.includes('\n');
                  
                  if (hasMultipleLines) {
                    // Mode structuré : parse les titres, listes, paragraphes
                    const lines = product.description.split('\n');
                    const elements = [];
                    let currentList = [];
                    
                    lines.forEach((line, idx) => {
                      const trimmed = line.trim();
                      if (!trimmed) return;
                      
                      // Détecte les titres (texte en MAJUSCULES)
                      if (trimmed === trimmed.toUpperCase() && trimmed.length > 3 && /^[A-Z\s']+$/.test(trimmed)) {
                        if (currentList.length > 0) {
                          elements.push(
                            <ul key={`ul-${idx}`} className="space-y-2 list-disc list-inside">
                              {currentList}
                            </ul>
                          );
                          currentList = [];
                        }
                        elements.push(
                          <h4 key={`title-${idx}`} className="text-base font-black dark:text-white mt-8 first:mt-0 mb-3 tracking-tight">
                            {trimmed}
                          </h4>
                        );
                      }
                      // Détecte les items de liste (commence par mot-clé + ":")
                      else if (trimmed.match(/^[A-Z][^:]+\s*:/)) {
                        const colonIndex = trimmed.indexOf(':');
                        currentList.push(
                          <li key={`li-${idx}`} className="ml-4">
                            <span className="font-bold">{trimmed.substring(0, colonIndex + 1)}</span>
                            {trimmed.substring(colonIndex + 1)}
                          </li>
                        );
                      }
                      // Paragraphe normal
                      else {
                        if (currentList.length > 0) {
                          elements.push(
                            <ul key={`ul-${idx}`} className="space-y-2 list-disc list-inside">
                              {currentList}
                            </ul>
                          );
                          currentList = [];
                        }
                        elements.push(
                          <p key={`p-${idx}`} className="text-justify leading-relaxed">
                            {trimmed}
                          </p>
                        );
                      }
                    });
                    
                    if (currentList.length > 0) {
                      elements.push(
                        <ul key="ul-last" className="space-y-2 list-disc list-inside">
                          {currentList}
                        </ul>
                      );
                    }
                    
                    return elements;
                  } else {
                    // Mode simple : découpe par phrases pour descriptions courtes
                    const sentences = product.description.split(/(?<=\.)\s+/).filter(s => s.trim());
                    return sentences.length > 1 ? (
                      sentences.map((sentence, idx) => (
                        <p key={idx} className="text-justify leading-relaxed">
                          {sentence.trim()}
                        </p>
                      ))
                    ) : (
                      <p className="text-justify leading-relaxed">{product.description}</p>
                    );
                  }
                })()}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-2xl">
              <h4 className="text-[10px] font-black uppercase text-slate-400 mb-2 flex items-center gap-2 tracking-widest">
                <ShieldCheck className="w-4 h-4 text-emerald-500" /> Garantie
              </h4>
              <p className="text-sm font-bold dark:text-slate-300">{product.warranty}</p>
            </div>

            {/* Documents & Ressources */}
            {( product.videoUrl || 
               (product.manuals && product.manuals.length > 0) || 
               (product.plans && product.plans.length > 0) || 
               (product.datasheets && product.datasheets.length > 0) ) && (
              <div className="space-y-4">
                <h3 className="font-black dark:text-white mb-4 flex items-center gap-2 uppercase tracking-widest text-xs text-slate-400">
                  <FileText className="w-4 h-4 text-blue-600" /> Ressources & Médias
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.videoUrl && (
                    <button
                      onClick={() => setShowVideo(true)}
                      className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-2xl border dark:border-slate-800 hover:border-red-500 transition-all group text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-600">
                          <Youtube className="w-4 h-4" />
                        </div>
                        <span className="text-xs font-bold dark:text-slate-300">Voir la Vidéo de présentation</span>
                      </div>
                      <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-red-600" />
                    </button>
                  )}
                  {product.manuals?.map((doc, idx) => (
                    <a key={idx} href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-2xl border dark:border-slate-800 hover:border-blue-500 transition-all group">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600">
                          <FileText className="w-4 h-4" />
                        </div>
                        <span className="text-xs font-bold dark:text-slate-300">{doc.label || 'Manuel d\'utilisation'}</span>
                      </div>
                      <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-blue-600" />
                    </a>
                  ))}
                  {product.plans?.map((doc, idx) => (
                    <a key={idx} href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-2xl border dark:border-slate-800 hover:border-blue-500 transition-all group">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-amber-600">
                          <Map className="w-4 h-4" />
                        </div>
                        <span className="text-xs font-bold dark:text-slate-300">{doc.label || 'Plan / Schéma'}</span>
                      </div>
                      <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-blue-600" />
                    </a>
                  ))}
                  {product.datasheets?.map((doc, idx) => (
                    <a key={idx} href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-2xl border dark:border-slate-800 hover:border-blue-500 transition-all group">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-emerald-600">
                          <FileText className="w-4 h-4" />
                        </div>
                        <span className="text-xs font-bold dark:text-slate-300">{doc.label || 'Fiche Technique'}</span>
                      </div>
                      <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-blue-600" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailView;
